import { WORKSPACE_STORAGE_TYPE } from '@bangle.io/constants';
import { useCoreServices } from '@bangle.io/context';
import {
  $stickyCloudWorkspace,
  $workspacePresencePeers,
} from '@bangle.io/editor';
import {
  fetchMyProfile,
  getSession,
  getUser,
  isSupabaseConfigured,
  joinWorkspacePresence,
  leaveWorkspacePresence,
  onAuthStateChange,
  pickPresenceColor,
  updateWorkspacePresenceLocal,
} from '@bangle.io/supabase';
import { useAtomValue, useSetAtom } from 'jotai';
import React from 'react';
import { useCloudWorkspaceAccess } from './use-cloud-workspace-access';

/**
 * Keeps sticky workspace-level presence alive while signed into a cloud
 * workspace — including settings and other non-editor routes — and mirrors
 * the open note path so peers can jump to each other's cursors.
 */
export function WorkspacePresenceController() {
  const core = useCoreServices();
  const setPeers = useSetAtom($workspacePresencePeers);
  const setStickyWorkspace = useSetAtom($stickyCloudWorkspace);
  const stickyWorkspace = useAtomValue($stickyCloudWorkspace);
  const { workspaceId: routeWorkspaceId } = useCloudWorkspaceAccess();
  const wsName = useAtomValue(core.workspaceState.$currentWsName);
  const routeInfo = useAtomValue(core.navigation.$routeInfo);
  const [signedIn, setSignedIn] = React.useState(false);

  React.useEffect(() => {
    if (!isSupabaseConfigured()) return;
    void getSession().then((session) => setSignedIn(Boolean(session?.user)));
    return onAuthStateChange((session) => setSignedIn(Boolean(session?.user)));
  }, []);

  // Stick to the last cloud workspace across settings; clear when entering a
  // non-cloud workspace or signing out.
  React.useEffect(() => {
    if (!signedIn) {
      setStickyWorkspace(null);
      return;
    }
    if (routeWorkspaceId && wsName) {
      setStickyWorkspace((prev) =>
        prev?.workspaceId === routeWorkspaceId && prev.wsName === wsName
          ? prev
          : { workspaceId: routeWorkspaceId, wsName },
      );
      return;
    }
    if (!wsName) return;
    let cancelled = false;
    void core.workspaceOps.getWorkspaceInfo(wsName).then((info) => {
      if (cancelled) return;
      if (
        info?.type === WORKSPACE_STORAGE_TYPE.Supabase &&
        typeof info.metadata.workspaceId === 'string'
      ) {
        const workspaceId = info.metadata.workspaceId;
        setStickyWorkspace((prev) =>
          prev?.workspaceId === workspaceId && prev.wsName === wsName
            ? prev
            : { workspaceId, wsName },
        );
      } else {
        setStickyWorkspace(null);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [
    core.workspaceOps,
    routeWorkspaceId,
    setStickyWorkspace,
    signedIn,
    wsName,
  ]);

  React.useEffect(() => {
    if (!isSupabaseConfigured() || !signedIn || !stickyWorkspace) {
      setPeers([]);
      void leaveWorkspacePresence();
      return;
    }

    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    void (async () => {
      const user = await getUser();
      if (cancelled || !user) return;
      const profile = await fetchMyProfile().catch(() => null);
      const name =
        profile?.display_name ||
        user.email ||
        (user.user_metadata?.display_name as string | undefined) ||
        'Collaborator';

      unsubscribe = await joinWorkspacePresence({
        workspaceId: stickyWorkspace.workspaceId,
        local: {
          id: user.id,
          name,
          color: pickPresenceColor(user.id),
          avatarUrl: profile?.avatar_url ?? null,
          filePath: null,
        },
        onPeers: (peers) => {
          if (!cancelled) setPeers(peers);
        },
      });
    })().catch((err) => {
      console.warn('workspace presence join failed', err);
      if (!cancelled) setPeers([]);
    });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [setPeers, signedIn, stickyWorkspace]);

  React.useEffect(() => {
    if (!stickyWorkspace || !signedIn) return;
    const filePath =
      routeInfo.route === 'editor' &&
      typeof routeInfo.payload?.wsPath === 'string'
        ? routeInfo.payload.wsPath.slice(
            routeInfo.payload.wsPath.indexOf(':') + 1,
          )
        : null;
    void updateWorkspacePresenceLocal({ filePath }).catch(() => {
      // Non-fatal — peers still see the user, just without a jump target.
    });
  }, [routeInfo, signedIn, stickyWorkspace]);

  React.useEffect(() => {
    return () => {
      void leaveWorkspacePresence();
      setPeers([]);
      setStickyWorkspace(null);
    };
  }, [setPeers, setStickyWorkspace]);

  return null;
}
